import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { i18next, view, hdrm, dialogs, elementSettings } from '@yola/ws-sdk';
import useDialogPosition from 'src/js/modules/dialogs/hooks/use-dialog-position';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import ElementSettingsDialog from '../components/element-settings-dialog';
import useSettingsManager from '../hooks/use-settings-manager';
import useSharedData from '../hooks/use-shared-data';
import useScrollToElement from '../hooks/use-scroll-to-element';
import { APPEARANCE_TAB_ID } from '../constants/common';
import errorNames from '../constants/error-names';
import trackers from '../trackers';
import ElementSettingsContext from '../helpers/element-settings-context';
import getAttributeActionsToApply from '../helpers/get-attribute-actions-to-apply';
import prepareElementSettings from '../helpers/prepare-element-settings';
import getTargetElementType from '../helpers/get-target-element-type';
import getAnalyticsTraits from '../helpers/get-analytics-traits';
import resolveAndMergeActions from '../helpers/resolve-and-merge-actions';
import runSubmitPreProcessingStage from '../helpers/stage-runners/run-submit-pre-processing-stage';
import runSubmitActionsGenerationStage from '../helpers/stage-runners/run-submit-actions-generation-stage';
import runSubmitTraitsGenerationStage from '../helpers/stage-runners/run-submit-traits-generation-stage';

const { UPDATE_LIVE_ONLY, UPDATE_BOTH } = hdrm.constants.updateStrategies;
const {
  trackElementSettingsDialogDisplayed,
  trackElementSettingsDialogCanceled,
  trackElementSettingsDialogSubmitted,
  trackElementSettingsTabChanged,
  trackElementSettingsControlClicked,
  trackElementSettingsToggleClicked,
  trackElementSettingsInputClicked,
} = trackers;

function ElementSettingsContainer({
  elements: elementsProp = null,
  offsetX: initialOffsetX = 0,
  offsetY: initialOffsetY = 0,
  getSharedData,
  resolveSharedData,
}) {
  const captions = useMemo(
    () => ({
      title: i18next.t('Settings'),
      buttons: {
        submit: i18next.t('Save'),
        cancel: i18next.t('Cancel'),
      },
    }),
    []
  );

  const dispatch = useDispatch();

  const { internalSharedData, externalSharedData, persistInternalSharedData, checkPersisting } =
    useSharedData(getSharedData, resolveSharedData);

  const elements = elementsProp || internalSharedData.elements;

  const { modalRef, modalOffset, onModalReady, onModalDragEnd } = useDialogPosition({
    element: elements[0],
    initialOffsetX: internalSharedData?.offsetX ?? initialOffsetX,
    initialOffsetY: internalSharedData?.offsetY ?? initialOffsetY,
  });
  const tabsScrollContainerRef = useRef(null);
  const { scrollTo } = useScrollToElement();
  const {
    state,
    form,
    stageHandlers,
    mountChunk,
    unmountChunk,
    isChunkMounted,
    getTabIdByChunkId,
    getPersistData,
  } = useSettingsManager({
    initialData: internalSharedData?.settingsManager,
    checkPersisting,
  });

  const matchedSettings = useMemo(() => {
    const settings = elementSettings.helpers.getElementSettings();

    return prepareElementSettings(elements, settings);
  }, [elements]);

  const tabsList = useMemo(
    () =>
      matchedSettings.reduce(
        (result, setting) => {
          if (setting.tabs) {
            setting.tabs.forEach((tab) => {
              if (tab.id.endsWith(APPEARANCE_TAB_ID)) {
                result.appearance.push(tab);
              } else {
                result.custom.push(tab);
              }
            });
          }

          return result;
        },
        {
          custom: [],
          appearance: [],
        }
      ),
    [matchedSettings]
  );

  const initialTab = useMemo(() => {
    if (internalSharedData?.activeTab) {
      return internalSharedData.activeTab;
    }

    const mainTab = matchedSettings[0]?.tabs?.[0];

    return mainTab?.id.endsWith(APPEARANCE_TAB_ID)
      ? APPEARANCE_TAB_ID
      : mainTab?.id || APPEARANCE_TAB_ID;
  }, [matchedSettings, internalSharedData]);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [visuallyHidden, setVisuallyHidden] = useState(false);

  const analyticsTraits = useMemo(
    () => getAnalyticsTraits(elements, matchedSettings),
    [elements, matchedSettings]
  );

  const getElementType = useCallback(
    (targetElements) => getTargetElementType(targetElements, matchedSettings),
    [matchedSettings]
  );

  // Track which accordion items the user has open so that we can restore them after
  // returning from ADD_COLOR_SCHEME_DIALOG / ADVANCED_COLORS_SETTINGS_DIALOG. The dialog
  // flow fully remounts this container, so the Accordion would otherwise reset to its
  // defaultActiveItem and potentially close accordion groups the user had expanded.
  const accordionActiveItemsRef = useRef(
    internalSharedData?.accordionActiveItems ?? tabsList.appearance[0]?.id ?? null
  );
  const handleAccordionToggle = useCallback((items) => {
    accordionActiveItemsRef.current = items;
  }, []);

  useEffect(() => {
    trackElementSettingsDialogDisplayed({
      ...analyticsTraits,
      tabId: initialTab,
      elementType: getElementType(elements),
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleUpdateState = useCallback(
    (batch, { chunkId = null }) => {
      const actions = getAttributeActionsToApply(batch, { strategy: UPDATE_LIVE_ONLY });
      dispatch(view.actions.bulkViewActions(actions));
      state.updateState(batch, { chunkId });

      const elementId = Object.keys(batch)[0];
      const elementToScroll = view.accessors.getLiveElement(elementId);

      scrollTo(elementToScroll);
    },
    [dispatch, state, scrollTo]
  );

  const handlePersistSharedData = useCallback(
    async (additionalData = {}, { preservePosition = false } = {}) => {
      const dataToSave = {
        ...additionalData,
        settingsManager: getPersistData(),
        activeTab,
        accordionActiveItems: accordionActiveItemsRef.current,
        offsetX: modalOffset.x,
        offsetY: modalOffset.y,
        elements,
      };

      return persistInternalSharedData(
        dataToSave,
        preservePosition ? { offsetX: modalOffset.x, offsetY: modalOffset.y } : {}
      );
    },
    [getPersistData, activeTab, modalOffset, persistInternalSharedData, elements]
  );

  const handleSubmission = useCallback(async () => {
    try {
      const { flowData: preProcessFlowData } = await runSubmitPreProcessingStage({
        state,
        form,
        onRun: stageHandlers.runStage,
        onPersistSharedData: handlePersistSharedData,
      });

      const { flowData: actionsFlowData } = await runSubmitActionsGenerationStage({
        state,
        form,
        flowData: preProcessFlowData,
        onRun: stageHandlers.runStage,
      });

      const { flowData } = await runSubmitTraitsGenerationStage({
        state,
        form,
        flowData: actionsFlowData,
        onRun: stageHandlers.runStage,
      });

      const stateDiff = state.getStateDiff();

      trackElementSettingsDialogSubmitted({
        ...analyticsTraits,
        tabId: activeTab,
        elementType: getElementType(elements),
        changedTraits: flowData.traits ?? {},
      });

      const customActions = flowData.actions ?? [];
      const attributeActions = getAttributeActionsToApply(stateDiff, { strategy: UPDATE_BOTH });
      const mergedActions = resolveAndMergeActions(customActions, attributeActions);

      if (mergedActions.length > 0) {
        dispatch(view.actions.bulkViewActions(mergedActions));
      }
    } catch (error) {
      if (error?.name !== errorNames.STAGED_HANDLER_ABORT_ERROR) {
        console.error(error);
      }
    }
  }, [
    state,
    stageHandlers,
    analyticsTraits,
    activeTab,
    dispatch,
    form,
    handlePersistSharedData,
    elements,
    getElementType,
  ]);

  const handleCancelation = useCallback(() => {
    trackElementSettingsDialogCanceled({
      ...analyticsTraits,
      tabId: activeTab,
      elementType: getElementType(elements),
    });

    dispatch(view.actions.forceReloadView());
  }, [analyticsTraits, dispatch, getElementType, elements, activeTab]);

  const handleErrors = useCallback(
    (error) => {
      if (!error || !error.errors) {
        return;
      }

      const errorFields = Object.keys(error.errors);
      const tabErrorsMap = new Map();
      errorFields.forEach((fieldName) => {
        const chunkId = form.getChunkIdByFieldName(fieldName);
        const tabId = getTabIdByChunkId(chunkId);

        if (tabId) {
          if (!tabErrorsMap.has(tabId)) {
            tabErrorsMap.set(tabId, []);
          }

          tabErrorsMap.get(tabId).push(fieldName);
        }
      });

      const isCurrentTabHasErrors = tabErrorsMap.has(activeTab);

      if (isCurrentTabHasErrors) {
        const currentTabErrors = tabErrorsMap.get(activeTab);
        form.setFocus(currentTabErrors[0]);
      } else if (tabErrorsMap.size > 0) {
        const firstTabWithErrors = Array.from(tabErrorsMap.keys())[0];
        const firstTabErrors = tabErrorsMap.get(firstTabWithErrors);

        setActiveTab(firstTabWithErrors);
        requestAnimationFrame(() => {
          form.setFocus(firstTabErrors[0]);
        });
      }
    },
    [form, getTabIdByChunkId, setActiveTab, activeTab]
  );

  const handleSubmit = useCallback(
    async ({ shouldScrollToTarget = false } = {}) => {
      try {
        await form.triggerFormValidation();

        if (shouldScrollToTarget) {
          await scrollTo(elements[0]);
          setVisuallyHidden(true);
        }

        await handleSubmission();

        dialogs.operations.hide();

        return true;
      } catch (error) {
        handleErrors(error);

        return false;
      }
    },
    [form, scrollTo, handleSubmission, handleErrors, elements]
  );

  const handleCancel = useCallback(
    async ({ shouldScrollToTarget = false } = {}) => {
      try {
        if (shouldScrollToTarget) {
          setVisuallyHidden(true);
          await scrollTo(elements[0]);
        }

        handleCancelation();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to cancel element settings dialog: ${error}`);
      } finally {
        dialogs.operations.hide();
      }
    },
    [handleCancelation, scrollTo, elements]
  );

  const handleOverlayClick = useCallback(async () => {
    try {
      await form.triggerFormValidation();
      await handleSubmit();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Validation failed, cancelling changes: ${error}`);
      await handleCancel();
    }
  }, [form, handleSubmit, handleCancel]);

  const handleControlClick = useCallback(
    ({ controlId, settingId, skipTracking = false }) => {
      if (!skipTracking) {
        trackElementSettingsControlClicked({
          ...analyticsTraits,
          settingId,
          tabId: activeTab,
          elementType: getElementType(elements),
          controlId,
        });
      }
    },
    [activeTab, analyticsTraits, elements, getElementType]
  );

  const handleToggleClick = useCallback(
    ({ controlId, settingId, isActive }) => {
      trackElementSettingsToggleClicked({
        ...analyticsTraits,
        settingId,
        tabId: activeTab,
        elementType: getElementType(elements),
        controlId,
        isActive,
      });
    },
    [activeTab, analyticsTraits, elements, getElementType]
  );

  const handleInputClick = useCallback(
    ({ settingId, controlId }) => {
      trackElementSettingsInputClicked({
        ...analyticsTraits,
        settingId,
        tabId: activeTab,
        elementType: getElementType(elements),
        controlId,
      });
    },
    [activeTab, analyticsTraits, elements, getElementType]
  );

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      trackElementSettingsTabChanged({
        ...analyticsTraits,
        tabId,
        elementType: getElementType(elements),
      });
    },
    [analyticsTraits, elements, getElementType]
  );

  const handleReopenDialog = useCallback(() => {
    dialogs.operations.show(dialogTypes.ELEMENT_SETTINGS_DIALOG, {
      elements,
      preserveSharedData: true,
    });
  }, [elements]);

  const callbacks = useMemo(
    () => ({
      onControlClick: handleControlClick,
      onToggleClick: handleToggleClick,
      onInputClick: handleInputClick,
    }),
    [handleControlClick, handleToggleClick, handleInputClick]
  );

  const dialog = useMemo(
    () => ({
      position: modalOffset,
      onReopen: handleReopenDialog,
    }),
    [handleReopenDialog, modalOffset]
  );

  const tabs = useMemo(
    () => ({
      activeId: activeTab,
      scrollContainerRef: tabsScrollContainerRef,
    }),
    [activeTab]
  );

  const sharedData = useMemo(
    () => ({
      internal: internalSharedData,
      external: externalSharedData,
      onPersist: handlePersistSharedData,
    }),
    [internalSharedData, externalSharedData, handlePersistSharedData]
  );

  if (!matchedSettings) return null;
  if (!elements.length) return null;

  return (
    <ElementSettingsContext.Provider
      value={{
        state: state.data,
        onUpdateState: handleUpdateState,
        onSubmit: handleSubmit,
        callbacks,
        dialog,
        tabs,
        analyticsTraits,
        form: {
          control: form.control,
          manageFields: form.manageFields,
          triggerFieldValidation: form.triggerFieldValidation,
          getValues: form.getValues,
          setValue: form.setValue,
          clearErrors: form.clearErrors,
        },
        sharedData,
        mountChunk,
        unmountChunk,
        isChunkMounted,
      }}
    >
      <ElementSettingsDialog
        modalRef={modalRef}
        offsetX={modalOffset.x}
        offsetY={modalOffset.y}
        captions={captions}
        tabs={tabsList}
        tabsActiveId={activeTab}
        tabsScrollContainerRef={tabsScrollContainerRef}
        visuallyHidden={visuallyHidden}
        accordionDefaultActiveItems={accordionActiveItemsRef.current}
        onAccordionToggle={handleAccordionToggle}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onOverlayClick={handleOverlayClick}
        onTabChange={handleTabChange}
        onDragEnd={onModalDragEnd}
        onReady={onModalReady}
      />
    </ElementSettingsContext.Provider>
  );
}

ElementSettingsContainer.propTypes = {
  elements: PropTypes.array,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default ElementSettingsContainer;
